<template>
    <card :title="title">
        <template slot="actions">
            <button
                type="button"
                class="btn btn-sm is-primary"
                @click="wantsToAddTask = true"
            >
                Lägg till uppgift
            </button>
        </template>

        <div v-if="tasks">
            <add-to-task-list
                v-if="wantsToAddTask"
                :task-list-id="id"
                @cancel="wantsToAddTask = false"
                @added="added"
            />

            <component
                :is="`${task.type}-task`"
                v-for="task in tasks"
                :key="task.id"
                :task="task"
                class="border-b border-neutral-lightest"
                :is-signed="taskList.requires_sign && taskList.signed_at !== null ? true : false"
                @finished="loadTasks"
            />
        </div>
        <em v-if="tasks == null">
            <span v-if="loading">Laddar...</span>
            <span v-else>
                Inga uppgifter...
            </span>
        </em>

        <div
            v-if="taskList.requires_sign"
            class="text-xs py-md"
        >
            <div
                v-if="! taskList.signed_at"
                class="flex items-center"
            >
                <font-awesome-icon
                    icon="times"
                    class="text-neutral-light mr-sm"
                />
                <span class="italic">Ej signerad</span>
            </div>
            <div
                v-else-if="taskList.signed_at && taskList.signed_by_user"
                class="flex items-center"
            >
                <font-awesome-icon
                    icon="check"
                    class="text-success mr-sm"
                />
                <span
                    v-if="taskList.all_tasks_were_complete_when_signed"
                    class="mr-sm"
                >
                    Signerad {{ signedDate(taskList.signed_at) }}
                </span>
                <span
                    v-else
                    class="mr-sm"
                >
                    Signerad med avvikelse {{ signedDate(taskList.signed_at) }}
                </span>
                <span class="font-bold">{{ taskList.signed_by_user.name }}</span>
            </div>
        </div>

        <div
            v-if="canBeSigned"
            class="flex justify-between items-center"
        >
            <div>
                <font-awesome-icon
                    icon="exclamation-circle"
                    class="mr-sm"
                />
                <span class="text-sm">Kräver signering</span>
            </div>
            <a
                :href="route(signRouteName, [taskList.model_id, taskList])"
                class="btn is-primary btn-lg"
            >
                Signera
            </a>
        </div>

        <slot />
    </card>
</template>

<script>
    import { format } from 'date-fns';

    import CheckboxTask from '../Types/CheckboxTask';
    import ImageTask from '../Types/ImageTask';
    import FileTask from '../Types/FileTask';
    import SelectTask from '../Types/SelectTask';
    import MultiSelectTask from '../Types/MultiSelectTask';
    import TextTask from '../Types/TextTask';
    import NumberTask from '../Types/NumberTask';

    import AddToTaskList from './AddToTaskList';

    export default {
        components: {
            CheckboxTask, // eslint-disable-line vue/no-unused-components
            ImageTask, // eslint-disable-line vue/no-unused-components
            FileTask, // eslint-disable-line vue/no-unused-components
            SelectTask, // eslint-disable-line vue/no-unused-components
            MultiSelectTask, // eslint-disable-line vue/no-unused-components
            TextTask, // eslint-disable-line vue/no-unused-components
            NumberTask, // eslint-disable-line vue/no-unused-components
            AddToTaskList, // eslint-disable-line vue/no-unused-components
        },
        props: {
            id: {
                required: true,
                type: Number,
            },
            taskList: {
                required: true,
                type: Object,
            },
            label: {
                type: String,
                default: '',
            },
            allowSign: {
                type: Boolean,
                default: false,
            },
            signRouteName: {
                type: String,
                default: '',
            },
        },
        data() {
            return {
                tasks: null,
                loading: false,
                canCompleteTaskList: false,
                wantsToAddTask: false,
            };
        },
        computed: {
            title() {
                if (! this.label) {
                    return this.taskList.name;
                }

                return `${this.taskList.name} - ${this.label}`;
            },
            canBeSigned() {
                return this.allowSign && this.taskList.requires_sign && ! this.taskList.signed_at && !! this.signRouteName;
            },
        },
        watch: {
            tasks: {
                initial: true,
                handler(newTasks) {
                    let localCanCompleteTaskList = true;
                    newTasks.forEach((task) => {
                        if (task.completed_at === null) {
                            localCanCompleteTaskList = false;
                        }
                    });

                    this.canCompleteTaskList = localCanCompleteTaskList;

                    EventBus.$emit('canCompleteTaskList', this.canCompleteTaskList);
                },
            },
        },
        created() {
            this.loadTasks();
        },
        methods: {
            async loadTasks() {
                this.loading = true;

                try {
                    const results = await axios.get(route('api.tasks.index', this.id));

                    this.tasks = results.data;
                } catch (e) {
                    flash_error('Kunde inte ladda ärenden');
                }

                this.loading = false;
            },
            added(data) {
                this.loadTasks();

                if (data.keepCreateView) {
                    return;
                }

                this.wantsToAddTask = false;
            },
            signedDate(date) {
                return format(date, 'YYYY-MM-DD');
            },
        },
    };
</script>
